import { addMinutes, isValid } from 'date-fns';

/** Valeurs saisies dans le panneau de qualification, avant envoi. */
export type QualificationDraft = {
  interventionTypeCode: number | null;
  /**
   * « Protocole cible » dans l'interface. Deux noms côté API : `target_radio_mode` au POST
   * de qualification, `radio_mode` au GET des configurations.
   */
  radioMode: number | null;
  /**
   * « Index horaires » dans l'interface. Même piège : `target_specific_criteria` au POST de
   * qualification, `target_criteria` au GET des configurations. Un nom inconnu du POST est
   * ignoré sans erreur, et la configuration « Par défaut » est alors résolue sans le critère.
   */
  targetCriteria: boolean | null;
  /** `null` pour « Par défaut », que l'API accepte telle quelle, un identifiant sinon. */
  targetConfiguration: number | null;
  cause: string;
  instructions: string;
  /**
   * Valeurs des pickers MUI : `null` si vide, `Invalid Date` tant que la saisie au
   * clavier est incomplète. Seules les heures et minutes des champs heure comptent.
   */
  startDate: Date | null;
  startTime: Date | null;
  endDate: Date | null;
  endTime: Date | null;
};

export const EMPTY_QUALIFICATION_DRAFT: QualificationDraft = {
  interventionTypeCode: null,
  radioMode: null,
  targetCriteria: null,
  targetConfiguration: null,
  cause: '',
  instructions: '',
  startDate: null,
  startTime: null,
  endDate: null,
  endTime: null
};

export type QualifyPayload = {
  target_radio_mode: number | null;
  target_specific_criteria: boolean | null;
  target_configuration: number | null;
  intervention_cause: string | null;
  intervention_instructions: string | null;
  /** Bornes du rendez-vous en UTC, `AAAA-MM-JJTHH:MI:SS.000Z`. */
  start_scheduled_time: string | null;
  end_scheduled_time: string | null;
  pds_ids: number[];
};

/**
 * Réponse du POST de qualification.
 *
 * Depuis que le type d'intervention a son propre endpoint, ce POST sauvegarde le
 * formulaire et fait passer à QUALIFIED en un seul appel — mais seulement pour les
 * demandes complètes. Les autres sont bien sauvegardées, et listées dans
 * `missing_fields` avec ce qui leur manque encore.
 */
export type QualifyResult = {
  total_submitted: number;
  success_count: number;
  successful_ids: number[];
  /** Demandes passées à QUALIFIED lors de cet appel. */
  qualified_ids: number[];
  /** Demandes sauvegardées mais incomplètes, indexées par id : `{"2": ["intervention_type_code"]}`. */
  missing_fields: Record<string, string[]>;
  failed_ids: number[];
  errors?: Record<string, string>[];
};

/** Corps du POST qui enregistre le seul type d'intervention, sans qualifier. */
export type SetInterventionTypePayload = {
  pds_ids: number[];
  intervention_type_code: number;
};

/**
 * Options du critère spécifique cible. Liste locale et non API, comme en v1 :
 * les trois valeurs sont un tri-état booléen.
 */
export const TARGET_CRITERIA_OPTIONS: { value: boolean | null; labelKey: string }[] = [
  { value: null, labelKey: 'interventionCreation.qualification.criteria.default' },
  { value: true, labelKey: 'interventionCreation.qualification.criteria.withHourlyIndex' },
  { value: false, labelKey: 'interventionCreation.qualification.criteria.withoutHourlyIndex' }
];

/** Même URL que le GET de la liste : seul le verbe distingue les deux. */
export function buildQualifyUrl(clientId: number): string {
  return `/api/boi/exocet/pds/qualify/?${new URLSearchParams({ customer_id: String(clientId) })}`;
}

/**
 * Fait passer les demandes cochées à l'étape Affectation sans passer par le formulaire.
 *
 * ⚠️ Homonyme de `buildTransitionUrl('send')` : même segment `upgrade-status/`, mais sous
 * `qualify/` et non `assign-technician/`. Celui-ci mène des Demandes à l'Affectation ;
 * l'autre crée les interventions et sort de l'écran. Les confondre enverrait les demandes
 * deux étapes trop loin.
 */
export function buildSendToAssignmentUrl(clientId: number): string {
  return `/api/boi/exocet/pds/qualify/upgrade-status/?${new URLSearchParams({
    customer_id: String(clientId)
  })}`;
}

/**
 * Enregistre le type d'intervention seul, sans faire changer le statut des PDS :
 * ce champ est sauvegardé dès sa sélection, avant tout clic sur « Qualifier ».
 */
export function buildSetInterventionTypeUrl(clientId: number): string {
  return `/api/boi/exocet/pds/qualify/set-intervention-type/?${new URLSearchParams({
    customer_id: String(clientId)
  })}`;
}

export function buildSetInterventionTypePayload(
  interventionTypeCode: number,
  selectedRequestIds: number[]
): SetInterventionTypePayload {
  return {
    pds_ids: selectedRequestIds,
    intervention_type_code: interventionTypeCode
  };
}

type TimeOfDay = { hours: number; minutes: number };

/** Date sans heure : « au plus tôt ce jour-là » part du début de journée… */
export const DEFAULT_START_TIME: TimeOfDay = { hours: 0, minutes: 0 };
/** …et « au plus tard ce jour-là » en couvre toute la journée. */
export const DEFAULT_END_TIME: TimeOfDay = { hours: 23, minutes: 59 };

/**
 * Date et heure saisies (heure locale du navigateur) vers l'ISO UTC attendu par l'API.
 * `null` sans date ou sur une saisie incomplète, que `getScheduleError` bloque à part.
 */
export function toScheduledTime(
  date: Date | null,
  time: Date | null,
  defaultTime: TimeOfDay
): string | null {
  if (!date || !isValid(date) || (time && !isValid(time))) {
    return null;
  }
  const scheduled = new Date(date);
  scheduled.setHours(
    time ? time.getHours() : defaultTime.hours,
    time ? time.getMinutes() : defaultTime.minutes,
    0,
    0
  );
  return scheduled.toISOString();
}

export type ScheduleError = 'incomplete' | 'startDateMissing' | 'endDateMissing' | 'endBeforeStart';

const isIncomplete = (value: Date | null) => value !== null && !isValid(value);

const DEFAULT_APPOINTMENT_DURATION_MINUTES = 30;

/**
 * Fin proposée au premier clic dans la fin : début + 30 min, ou la date de début seule
 * s'il n'a pas d'heure. `null` si la fin est déjà entamée ou le début inexploitable.
 */
export function suggestAppointmentEnd(
  draft: QualificationDraft
): Pick<QualificationDraft, 'endDate' | 'endTime'> | null {
  const { startDate, startTime, endDate, endTime } = draft;
  if (endDate || endTime || !startDate || !isValid(startDate) || isIncomplete(startTime)) {
    return null;
  }
  if (!startTime) {
    return { endDate: startDate, endTime: null };
  }
  const start = new Date(startDate);
  start.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
  const end = addMinutes(start, DEFAULT_APPOINTMENT_DURATION_MINUTES);
  return { endDate: end, endTime: end };
}

/** Erreur bloquante du rendez-vous, `null` si la saisie est envoyable. */
export function getScheduleError(draft: QualificationDraft): ScheduleError | null {
  if ([draft.startDate, draft.startTime, draft.endDate, draft.endTime].some(isIncomplete)) {
    return 'incomplete';
  }
  if (draft.startTime && !draft.startDate) {
    return 'startDateMissing';
  }
  if (draft.endTime && !draft.endDate) {
    return 'endDateMissing';
  }
  const start = toScheduledTime(draft.startDate, draft.startTime, DEFAULT_START_TIME);
  const end = toScheduledTime(draft.endDate, draft.endTime, DEFAULT_END_TIME);
  // Même format ISO UTC des deux côtés : l'ordre lexicographique est l'ordre chronologique.
  if (start && end && end <= start) {
    return 'endBeforeStart';
  }
  return null;
}

/**
 * Les champs non renseignés partent explicitement à `null`, comme l'attend l'API :
 * un motif ou des instructions vides ne doivent pas arriver en chaîne vide.
 */
export function buildQualifyPayload(
  draft: QualificationDraft,
  selectedRequestIds: number[]
): QualifyPayload {
  return {
    target_radio_mode: draft.radioMode,
    target_specific_criteria: draft.targetCriteria,
    target_configuration: draft.targetConfiguration,
    intervention_cause: draft.cause.trim() || null,
    intervention_instructions: draft.instructions.trim() || null,
    start_scheduled_time: toScheduledTime(draft.startDate, draft.startTime, DEFAULT_START_TIME),
    end_scheduled_time: toScheduledTime(draft.endDate, draft.endTime, DEFAULT_END_TIME),
    pds_ids: selectedRequestIds
  };
}

/**
 * Valeur du select pour « Par défaut » : MUI n'accepte pas `null` comme valeur
 * d'option, et la chaîne vide passerait pour un champ non renseigné.
 */
export const DEFAULT_CONFIGURATION_VALUE = 'default';

/** État du formulaire vers la valeur affichée par le select. */
export function configurationToSelectValue(value: number | null): string {
  return value === null ? DEFAULT_CONFIGURATION_VALUE : String(value);
}

/** Valeur choisie dans le select vers l'état du formulaire. */
export function selectValueToConfiguration(value: string): number | null {
  return value === DEFAULT_CONFIGURATION_VALUE ? null : Number(value);
}

/**
 * Les filtres passés à `useGetConfigurations`. DST et AutoConnect ont été abandonnés :
 * seuls le protocole et le critère restreignent encore les configurations proposées.
 */
export function buildConfigurationFilters(draft: QualificationDraft): {
  radio_mode: number | null;
  target_criteria: boolean | null;
} {
  return {
    radio_mode: draft.radioMode,
    target_criteria: draft.targetCriteria
  };
}
